import { getSubTotalPrice } from '@/api/cart';
import LensImage from '@/components/LensImage';
import Popup from '@/components/Popup';
import ProductItemTag from '@/components/Product/ProductItemTag';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { isShowMemberOnlyTag } from '@/utils/tools';
import { RightArrowOutlined } from '@zeelool/icon';
import { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslations } from 'next-intl';

interface ProductListMobileProps {
  formattedList: any[];
  allQty: number;
  showList: boolean;
  toggleShowList: () => void;
  giftNum: number;
  redeemCartId: string | null;
  ref: React.RefObject<any>;
}

export default function ProductListMobile({
  formattedList,
  allQty,
  showList,
  toggleShowList,
  giftNum,
  redeemCartId,
  ref,
}: ProductListMobileProps) {
  const globalT = useTranslations('');
  const t = useTranslations('payment.ProductList');
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  // const landingPage = useStore((state) => state.landingPage);
  const selectedCart = useStore((state) => state.selectedCartIds);

  const [subTotal, setSubTotal] = useState<any>({});

  // 对外暴露subTotal
  useImperativeHandle(ref, () => ({
    subTotal,
    selectedListFormPriceApi,
  }));

  const [selectedListFormPriceApi, setSelectedListFormPriceApi] = useState<any[]>([]);

  const handleSelectedCart = async () => {
    setSelectedListFormPriceApi([]);
    if (redeemCartId) return;
    if (selectedCart?.length) {
      try {
        const { data } = await getSubTotalPrice({
          currency: currency.name,
          cart_ids: selectedCart,
        });
        setSubTotal(data);
        setSelectedListFormPriceApi(data.tmp.carts);
      } catch (err) {
        logger.log(err);
      }
    }
  };

  useEffect(() => {
    handleSelectedCart();
  }, [selectedCart]);

  const getType = (item: any): 3 | 4 | 5 | 6 | 7 | 'extra' | 'member_only' => {
    const { product_group } = item.product_info;
    const member_only = isShowMemberOnlyTag({
      userInfo,
      landingPage: {},
      productGroup: product_group,
    });
    if (item.type === 6) return; // 积分兑换订单不显示标签
    try {
      if (selectedListFormPriceApi.length) {
        for (const ele of selectedListFormPriceApi) {
          if (
            (ele.id === item.id &&
              ele.products_promotions_show === 1 &&
              ele.products_promotions_qty > 0 &&
              Number(ele.products_promotions_type) === 3) ||
            (ele.products_promotions_show === 2 && Number(ele.products_promotions_type) === 3)
          ) {
            return 6;
          }
        }
      }
    } catch (err) {
      logger.log(err);
    }
    if (item.id === 0) {
      return 'extra';
    }
    if (member_only) {
      return 'member_only';
    }
    return item.type as 3 | 4 | 5 | 6 | 7 | 'extra' | 'member_only';
  };

  const getLabelText = (item: any) => {
    if (item.type === 6) return '';
    try {
      if (selectedListFormPriceApi.length) {
        for (const ele of selectedListFormPriceApi) {
          if (
            (ele.id === item.id &&
              ele.products_promotions_show === 1 &&
              ele.products_promotions_qty > 0 &&
              [3, 4].includes(Number(ele.products_promotions_type))) ||
            (ele.products_promotions_show === 2 &&
              [3, 4].includes(Number(ele.products_promotions_type)))
          ) {
            return ele.product_info.activity_style.product_item_rules_text || 'Seckill';
          }
        }
      }
    } catch (err) {
      logger.log(err);
    }
    return '';
  };
  // 图片展示是有优先级的
  const getPreviewImage = (item) => {
    let originGlassImg = '';
    // 优先使用灰底图，没有再使用普通图片
    if (item?.image?.product_gray && item.image.product_gray.length > 0) {
      originGlassImg = item.image.product_gray[0];
    } else {
      originGlassImg = item?.image_gray || item?.img || item?.image;
    }

    return originGlassImg;
  };

  return (
    <>
      <div className="bg-white  flex items-center justify-between" onClick={toggleShowList}>
        {/* left image list  */}
        <div className="flex items-center gap-10 flex-1">
          {formattedList.slice(0, 3).map((item: any) => (
            <div
              key={item.id}
              className="border-1 border-solid border-gray-200 rounded-4 w-[70px] h-[70px] bg-[#eee] flex items-center justify-center overflow-hidden"
            >
              <LensImage
                product={item.product_info}
                imageUrl={getPreviewImage(item.product_info)}
                lens={item.lens_info}
                isGlass={+item.product_info.is_glass === 1}
                onlyShowImage
              />
            </div>
          ))}
        </div>

        {/* right text show  */}
        <div className="text-primary-black font-br text-14">
          <div className="flex items-center">
            <div>
              <div>
                {allQty}&nbsp;{allQty > 1 ? t('Items') : t('Item')}
              </div>
              {Number(giftNum) > 0 && (
                <div className="mt-4">
                  ({t('Gift')} x{giftNum || 0})
                </div>
              )}
            </div>
            <RightArrowOutlined className="text-16" />
          </div>
        </div>
      </div>

      <Popup open={showList} title={t('ProductList')} position="bottom" onClose={toggleShowList}>
        <div className="max-h-[70vh] overflow-auto p-4">
          {formattedList.map((item: any, index) => (
            <div key={`${item.id}-${index}`} className="w-full flex py-10  gap-8">
              <div className="bg-[#eee] mr-3 w-[100px] h-[100px] flex items-center justify-center relative border-1 border-solid border-[#d0d4db] rounded-8 overflow-hidden">
                <LensImage
                  product={item.product_info}
                  imageUrl={getPreviewImage(item.product_info)}
                  lens={item.lens_info}
                  isGlass={+item.product_info.is_glass === 1}
                  onlyShowImage
                />
                <div className="absolute top-0 left-0">
                  <ProductItemTag type={getType(item)} labelText={getLabelText(item)} />
                </div>

                <span className="sr-only">zeelool glasses</span>
              </div>
              <div className="flex-1 relative mt-8">
                <div className="font-bb flex items-center justify-between">
                  <div className="text-14  mb-10">{item.product_info.name}</div>
                  <span className="">{item.formattedPrice}</span>
                </div>
                {item.hasColor && (
                  <p className="text-xs mb-10">
                    {t('color')}: {item.product_info.color_name || item.product_info.color}
                  </p>
                )}

                {item.product_info.is_glass === 1 &&
                  item.option.prescription_type &&
                  item.option.prescription_type !== EPrescriptionType.FRAME_ONLY && (
                    <p className="text-xs text-gray-600">
                      RX: {item.option.prescription_type_name}
                    </p>
                  )}

                <p className="flex items-center justify-between text-[12px] mt-1">
                  <span>
                    {globalT('cart.cartItem.qty')} {item.qty}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </Popup>
    </>
  );
}
